import { BaseWebComponentView } from '../../../../framework/BaseWebComponentView';
import { GzTabGroupWebComponent } from '../../../../ui/gzTabs/gzTabGroup';
import { GzTabWebComponent } from '../../../../ui/gzTabs/gzTab';
import {
  WEB_COMPONENT_COMPANY_AUTHENTICATION_TAB,
  WEB_COMPONENT_COMPANY_DETAILS_TAB,
  WEB_COMPONENT_COMPANY_LICENSING_TAB,
  WEB_COMPONENT_COMPANY_MSP_IN_PRODUCT_TRIALS_TAB,
} from '../../../../webcomponents.constants';
import { CompanyData } from '../../../types/company-data';
import { CompanyAuthenticationTabWebComponent } from '../../../common/authenticationTab';
import { CompanyLicensingTabWebComponent } from '../../../common/licensingTab';
import { TemplateState } from '../../../../types/common/types';
import { GzTitleWebComponent } from '../../../../ui/gzTitle';
import { CompanyEditingRight } from '../../../common/types/CompanyEditingRight';
import { CompanyActionContext } from '../../../common/types/CompanyActionContext';
import { CompanyDetailsTabWebComponent } from '../../../common/companyDetailsTab';
import { GzDialogWebComponent } from '../../../../ui/gzDialog';
import { CompanyMspInProductTrialsTabWebComponent } from '../../../common/mspInProductTrialsTab';
import { CompanyType } from '../../../types/company-type';
import { SubscriptionType } from '../../../../license/types/SubscriptionType';
import { TabIndex } from './EditCompanyPanelTypes';

export class EditCompanyPanelView extends BaseWebComponentView {
  public sideMenuParentDialog: GzDialogWebComponent;

  public dom: {
    $wrapper: HTMLElement;

    $errorMessage: GzTitleWebComponent;

    $tabGroup: GzTabGroupWebComponent;

    companyDetails: {
      $companyDetailsGzTab: GzTabWebComponent;
      $companyDetailsForm: CompanyDetailsTabWebComponent;
    };

    $authenticationGzTab: GzTabWebComponent;
    $licensingGzTab: GzTabWebComponent;
    $mspInProductTrialsGzTab: GzTabWebComponent;

    $authentication: CompanyAuthenticationTabWebComponent;
    $licensing: CompanyLicensingTabWebComponent;
    $mspInProductTrials: CompanyMspInProductTrialsTabWebComponent;
  };

  private componentsStarted = false;
  private indexToTabComponentMapping = {};

  getDomElements() {
    this.dom = {
      $wrapper: this.shadow.querySelector('.wrapper'),
      $errorMessage: this.shadow.querySelector('#error-message'),

      $tabGroup: this.shadow.querySelector('#edit-company-tab-group'),

      companyDetails: {
        $companyDetailsGzTab: this.shadow.querySelector('#company-details-tab'),
        $companyDetailsForm: this.shadow.querySelector(WEB_COMPONENT_COMPANY_DETAILS_TAB),
      },

      $authenticationGzTab: this.shadow.querySelector('#authentication-tab'),
      $licensingGzTab: this.shadow.querySelector('#licensing-tab'),
      $mspInProductTrialsGzTab: this.shadow.querySelector('#msp-in-product-trials-tab'),

      $authentication: this.shadow.querySelector(WEB_COMPONENT_COMPANY_AUTHENTICATION_TAB),
      $licensing: this.shadow.querySelector(WEB_COMPONENT_COMPANY_LICENSING_TAB),
      $mspInProductTrials: this.shadow.querySelector(WEB_COMPONENT_COMPANY_MSP_IN_PRODUCT_TRIALS_TAB),
    };

    this.getTranslations();
    this.setTranslatedStrings();
    this.setLoadingState();
  }

  getTranslations() {
    this.translations = this.stringsTranslator.getMyCompanyStrings();
    this.commonStrings = this.stringsTranslator.getCommonStrings();
  }

  setTranslatedStrings() {
    this.dom.companyDetails.$companyDetailsGzTab.label = this.translations.companyDetailsTitle;

    this.dom.$authenticationGzTab.label = this.translations.authenticationTitle;
    this.dom.$licensingGzTab.label = this.translations.licensingTitle;
    this.dom.$mspInProductTrialsGzTab.label = this.translations.mspInProductTrialsTitle;
  }

  public setLoadingState(): void {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.LOADING);
  }

  public removeLoadingState(): void {
    this.resetTemplateState(this.dom.$wrapper);
  }

  buildSuccessView({ company }: { company: CompanyData }) {
    this.dom.companyDetails.$companyDetailsForm.injectDependencies(this.dependencies);
    this.dom.companyDetails.$companyDetailsForm.company = company;

    this.dom.$authentication.injectDependencies(this.dependencies);
    this.dom.$authentication.companyId = company._id;
    this.dom.$authentication.editingRight = CompanyEditingRight.getCompanyEditingRight(
      company.readOnly ?? false,
      company.specifics.canBeManagedByAbove,
      CompanyActionContext.Edit
    );

    this.dom.$licensing.injectDependencies(this.dependencies);
    this.dom.$licensing.initialCompanyData = company;
    this.dom.$licensing.sideMenuParentDialog = this.sideMenuParentDialog;

    this.dom.$mspInProductTrials.companyId = company._id;
    this.dom.$mspInProductTrials.injectDependencies(this.dependencies);
    this.dom.$mspInProductTrials.setTrialFeatures(company.specifics.features, company.specifics.mspInProductTrials);

    this.changeMspInProductTrialsTabVisibility(company);

    // start all sub-components
    Promise.all([
      this.dom.companyDetails.$companyDetailsForm.start(),
      this.dom.$authentication.start(),
      this.dom.$licensing.start(),
      this.dom.$mspInProductTrials.start(),
    ])
      .then(() => {
        this.onComponentsStarted(company);
      })
      .catch(console.error);
  }

  public changeMspInProductTrialsTabVisibility(company: CompanyData) {
    const isMspInProductTrialsTabVisible =
      company.companyType === CompanyType.Customer &&
      CompanyData.determineCompanySubscription(company) === SubscriptionType.Inherited &&
      !company.specifics.features.bundleMspSecureUsable &&
      !company.specifics.features.bundleMspSecurePlusUsable &&
      !company.specifics.features.bundleMspSecureExtraUsable;

    if (!isMspInProductTrialsTabVisible) {
      this.dom.$tabGroup.hideTabs([TabIndex.MSP_IN_PRODUCT_TRIALS]);
    } else {
      this.dom.$tabGroup.showTabs([TabIndex.MSP_IN_PRODUCT_TRIALS]);
    }
  }

  private onComponentsStarted(company: CompanyData) {
    this.buildIndexToTabComponentMapping();

    this.removeLoadingState();
    this.componentsStarted = true;

    this.setCompanyInfoForMspProductTrials(company);
  }

  private setCompanyInfoForMspProductTrials(company: CompanyData) {
    this.dom.$mspInProductTrials.setCompanySelectedCountry(company.specifics.country);
    this.dom.$mspInProductTrials.setCompanySelectedIndustry(company.specifics.industry as number);
    this.dom.$mspInProductTrials.setCompanySelectedState(company.specifics.state);
  }

  public buildErrorView(error: Error) {
    this.dom.$errorMessage.innerText = this.dependencies.translateFn('Company not found or is not accessible');
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.ERROR);
  }

  public get hasUnsavedChanges(): boolean {
    if (!this.componentsStarted) {
      // can happen when the error view is shown or if user exits too fast
      return false;
    }

    // if any form has unsaved changes, return true
    return (
      this.dom.companyDetails.$companyDetailsForm.hasUnsavedChanges ||
      this.dom.$authentication.hasUnsavedChanges ||
      this.dom.$licensing.hasUnsavedChanges
    );
  }

  public get isVideoPlaying(): boolean {
    return this.dom.$mspInProductTrials.isVideoPlaying;
  }

  public getIndexToTabComponentMapping() {
    return this.indexToTabComponentMapping;
  }

  private buildIndexToTabComponentMapping() {
    this.indexToTabComponentMapping = {
      [TabIndex.DETAILS]: {
        component: this.dom.companyDetails.$companyDetailsForm,
        title: this.dom.companyDetails.$companyDetailsGzTab.label,
      },
      [TabIndex.AUTHENTICATION]: {
        component: this.dom.$authentication,
        title: this.dom.$authenticationGzTab.label
      },
      [TabIndex.LICENSING]: {
        component: this.dom.$licensing,
        title: this.dom.$licensingGzTab?.label
      },
      [TabIndex.MSP_IN_PRODUCT_TRIALS]: {
        component: this.dom.$mspInProductTrials,
        title: this.dom.$mspInProductTrialsGzTab?.label,
      },
    };
  }
}
